'use client'

import { ComponentProps, FunctionComponent, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Loader from '@/components/ui/loader'
import { ThemeProvider, Theme } from '@aws-amplify/ui-react'
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness'
import { initKycSession, validateKycSession } from '@/services/mobi-api/confirmation'
import { configureAmplify } from '@/context/AmplifyContext'
import '@aws-amplify/ui-react/styles.css'
import useIntl from '@/utils/intl'

import Dialog from '@mui/material/Dialog'
import Slide from '@mui/material/Slide'
import type { TransitionProps } from '@mui/material/transitions'
import { trackNewNumberEvent } from '@/utils/newNumberFlowTracking'

interface Props {
  regno: string
  instanceId?: string
  onContinue: () => void
  onBackToChoose: () => void
}

type Screen = 'intro' | 'liveness'
type LivenessDisplayText = NonNullable<ComponentProps<typeof FaceLivenessDetector>['displayText']>

type FailReason = 'generic' | 'comparison' | 'liveness' | 'tooManyAttempts' | 'cameraPermission'

type FailMessageKey =
  | 'verify.kyc.faceRecognition.failed'
  | 'verify.kyc.faceRecognition.failed.comparison'
  | 'verify.kyc.faceRecognition.failed.liveness'
  | 'verify.kyc.faceRecognition.failed.tooManyAttempts'
  | 'verify.kyc.faceRecognition.permission.text'
const REGION = 'ap-northeast-1'

const Transition = Slide as React.ComponentType<
TransitionProps & { children: React.ReactElement<any, any> }
>

const livenessTheme: Theme = {
  name: 'MobiLiveness',
  tokens: {
  }
}

const FaceKycStep: FunctionComponent<Props> = ({ regno, instanceId, onContinue, onBackToChoose }) => {
  const { translate } = useIntl()

  const [screen, setScreen] = useState<Screen>('intro')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const isHandlingError = useRef(false)
  const [failDialogOpen, setFailDialogOpen] = useState(false)
  const [shouldRetry, setShouldRetry] = useState(false)
  const failReasonToKey: Record<FailReason, FailMessageKey> = {
    generic: 'verify.kyc.faceRecognition.failed',
    comparison: 'verify.kyc.faceRecognition.failed.comparison',
    liveness: 'verify.kyc.faceRecognition.failed.liveness',
    tooManyAttempts: 'verify.kyc.faceRecognition.failed.tooManyAttempts',
    cameraPermission: 'verify.kyc.faceRecognition.permission.text'
  }
  const [failReason, setFailReason] = useState<FailReason>('generic')

  useEffect(() => {
    configureAmplify()
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const isInAppWebview = (window as any).mobiBridge != null
    if (!isInAppWebview) return

    try {
      const allKeys = Object.keys(localStorage)
      console.log('[FaceKYC] localStorage keys before cleanup:', allKeys)

      allKeys
        .filter(k => k === 'AmplifyLivenessCameraId')
        .forEach(k => {
          console.log('[FaceKYC] Removing storage key:', k)
          localStorage.removeItem(k)
        })
    } catch (e) {
      console.warn('[FaceKYC] Storage cleanup failed:', e)
    }
  }, [])

  const mnDisplayText: LivenessDisplayText = {
    hintTooFarText: translate('verify.kyc.faceRecognition.text.hintTooFarText'),
    waitingCameraPermissionText: translate('verify.kyc.faceRecognition.text.waitingCameraPermissionText'),
    hintConnectingText: translate('verify.kyc.faceRecognition.text.hintConnectingText'),
    tooFarCaptionText: translate('verify.kyc.faceRecognition.text.tooFarCaptionText'),
    hintVerifyingText: translate('verify.kyc.faceRecognition.text.hintVerifyingText'),
    hintMoveFaceFrontOfCameraText: translate('verify.kyc.faceRecognition.text.hintMoveFaceFrontOfCameraText'),
    hintTooCloseText: translate('verify.kyc.faceRecognition.text.hintTooCloseText'),
    hintHoldFaceForFreshnessText: translate('verify.kyc.faceRecognition.text.hintHoldFaceForFreshnessText'),
    recordingIndicatorText: '',

    // ------
    hintTooManyFacesText: '1',
    hintFaceDetectedText: '2',
    hintCanNotIdentifyText: '3',
    hintCheckCompleteText: '4',
    hintIlluminationTooBrightText: '5',
    hintIlluminationTooDarkText: '6',
    hintIlluminationNormalText: '7',
    hintCenterFaceText: '8',
    hintCenterFaceInstructionText: '9',
    hintFaceOffCenterText: '10',
    hintMatchIndicatorText: '11',

    cameraMinSpecificationsHeadingText: '12',
    cameraMinSpecificationsMessageText:
      '13',
    cameraNotFoundHeadingText: 'Камер олдсонгүй',
    cameraNotFoundMessageText:
      '14',
    retryCameraPermissionsText: '15',
    a11yVideoLabelText: '16',

    goodFitCaptionText: '17',
    goodFitAltText: '18',
    photosensitivityWarningBodyText:
      'Энэ шалгалтын үед өнгө хурдан өөрчлөгдөх тул гэрэлд мэдрэмтгий бол болгоомжтой ашиглана уу.',
    photosensitivityWarningHeadingText: '20',
    photosensitivityWarningInfoText:
      '21',
    photosensitivityWarningLabelText: '22',
    startScreenBeginCheckText: '23',
    tooFarAltText: '24',

    errorLabelText: '25.',
    connectionTimeoutHeaderText: '26',
    connectionTimeoutMessageText:
      '27',
    timeoutHeaderText: '28',
    timeoutMessageText:
      '29',
    faceDistanceHeaderText: '30',
    faceDistanceMessageText:
      '31',
    multipleFacesMessageText: '32',
    clientHeaderText: '33',
    clientMessageText:
      '34.',
    serverHeaderText: '35',
    serverMessageText:
      '36',
    landscapeHeaderText: '37',
    landscapeMessageText: '38.',
    portraitMessageText: '39',
    tryAgainText: '40',

    cancelLivenessCheckText: '42'
  }

  const startLivenessAttempt = async (): Promise<void> => {
    if (loading) return

    try {
      trackNewNumberEvent('start_verification', {
        step_name: 'identity_verification',
        method: 'face_id'
      })
      setLoading(true)
      setSessionId(null)

      if (instanceId == null || instanceId.trim().length === 0) {
        console.error('[FaceKYC] Missing instanceId')
        throw new Error('Instance ID is required')
      }

      const res = await initKycSession({ regno, instanceId })

      if (res?.status === 'COMPLETED' && res?.verified === true) {
        trackNewNumberEvent('verification_success', {
          step_name: 'identity_verification',
          method: 'face_id'
        })
        trackNewNumberEvent('step_complete', {
          step_name: 'identity_verification'
        })
        onContinue()
        return
      }

      if (res?.sessionId == null || res.sessionId.trim() === '') {
        console.error('[FaceKYC] No sessionId from initKycSession', res)

        const rawErrorCode = (res as any)?.errorCode
        const errorCode =
        typeof rawErrorCode === 'string' ? parseInt(rawErrorCode, 10) : rawErrorCode

        if (errorCode === 998) {
          trackNewNumberEvent('verification_fail', {
            step_name: 'identity_verification',
            method: 'face_id',
            error_reason: 'too_many_attempts'
          })
          setFailReason('tooManyAttempts')
        } else {
          trackNewNumberEvent('verification_fail', {
            step_name: 'identity_verification',
            method: 'face_id',
            error_reason: 'kyc_session_init_failed'
          })
          setFailReason('generic')
          setShouldRetry(true)
        }

        setFailDialogOpen(true)
        setScreen('intro')
        return
      }

      setSessionId(res.sessionId)
      setScreen('liveness')
    } catch (e) {
      trackNewNumberEvent('verification_fail', {
        step_name: 'identity_verification',
        method: 'face_id',
        error_reason: 'kyc_session_init_failed'
      })
      console.error('[FaceKYC] initKycSession failed', e)
      setFailDialogOpen(true)
      setScreen('intro')
      setShouldRetry(true)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenCamera = (): void => {
    if (shouldRetry) {
      trackNewNumberEvent('retry_action', {
        step_name: 'identity_verification',
        action_name: 'face_id'
      })
    }
    void startLivenessAttempt()
  }

  const isCameraAccessError = (error: unknown): boolean => {
    if (error == null || typeof error !== 'object') return false

    const livenessState = (error as { state?: string }).state
    if (livenessState === 'CAMERA_ACCESS_ERROR') return true

    const err = (error as { error?: Error }).error
    const name = err?.name ?? (error as { name?: string }).name
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') return true

    const message = err?.message ?? (error as { message?: string }).message ?? ''
    return message.toLowerCase().includes('permission denied')
  }

  const handleAnalysisComplete = async (): Promise<void> => {
    if (sessionId == null || sessionId === '') return

    setLoading(true)
    try {
      if (instanceId == null) {
        throw new Error('Instance ID is required')
      }
      const res = await validateKycSession({ sessionId, instanceId })
      const isVerified = res?.verified === true
      const errorCause = res?.errorCause
      const retriesLeft = res?.retriesLeft

      if (isVerified) {
        trackNewNumberEvent('verification_success', {
          step_name: 'identity_verification',
          method: 'face_id'
        })
        trackNewNumberEvent('step_complete', {
          step_name: 'identity_verification'
        })
        onContinue()
        return
      }

      if (retriesLeft === 0) {
        setFailReason('tooManyAttempts')
      } else if (errorCause === 'COMPARISON') {
        setFailReason('comparison')
      } else if (errorCause === 'LIVENESS') {
        setFailReason('liveness')
      } else {
        setFailReason('generic')
      }

      trackNewNumberEvent('verification_fail', {
        step_name: 'identity_verification',
        method: 'face_id',
        error_reason:
          retriesLeft === 0
            ? 'too_many_attempts'
            : errorCause?.toLowerCase() ?? 'generic'
      })

      console.warn('KYC failed', res)
      setFailDialogOpen(true)
      setScreen('intro')
      setSessionId(null)
      setShouldRetry(true)
    } catch (err) {
      trackNewNumberEvent('verification_fail', {
        step_name: 'identity_verification',
        method: 'face_id',
        error_reason: 'kyc_validation_failed'
      })
      console.error('[FaceKYC] validateKycSession failed', err)
      setFailReason('generic')
      setFailDialogOpen(true)
      setScreen('intro')
      setSessionId(null)
      setShouldRetry(true)
    } finally {
      setLoading(false)
    }
  }

  const handleError = (error: unknown): void => {
    console.error('Liveness error:', error)

    try {
      if (typeof window !== 'undefined' && (window as any).mobiBridge != null) {
        (window as any).mobiBridge.postMessage(
          JSON.stringify({
            key: 'liveness-error',
            value: error
          })
        )
      }
    } catch (e) {
      console.error('Failed to post liveness error to app', e)
    }

    if (isHandlingError.current) return
    isHandlingError.current = true

    try {
      setLoading(false)
      setSessionId(null)
      if (isCameraAccessError(error)) {
        trackNewNumberEvent('verification_fail', {
          step_name: 'identity_verification',
          method: 'face_id',
          error_reason: 'camera_permission'
        })
        setFailReason('cameraPermission')
      } else {
        trackNewNumberEvent('verification_fail', {
          step_name: 'identity_verification',
          method: 'face_id',
          error_reason: 'face_liveness_error'
        })
      }
      setFailDialogOpen(true)
      setScreen('intro')
      setShouldRetry(true)
    } finally {
      isHandlingError.current = false
    }
  }

  if (screen === 'liveness') {
    return (
      <ThemeProvider theme={livenessTheme}>
        <div className='flex flex-col items-center justify-center px-4 py-8 bg-surfaceLow'>
          {loading || sessionId == null || sessionId === ''
            ? (
              <div className='flex justify-center items-center h-64'>
                <Loader />
              </div>
              )
            : (
              <FaceLivenessDetector
                sessionId={sessionId}
                region={REGION}
                disableStartScreen
                displayText={mnDisplayText}
                onAnalysisComplete={handleAnalysisComplete}
                onError={handleError}
              />
              )}
        </div>
      </ThemeProvider>
    )
  }

  return (
    <>
      <Dialog
        open={failDialogOpen}
        TransitionComponent={Transition}
        keepMounted
        fullWidth
        maxWidth='xs'
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
          setFailDialogOpen(false)
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            px: { xs: 2, sm: 4 },
            py: { xs: 3, sm: 4 },
            backgroundColor: 'rgb(var(--surfaceInverse))'
          }
        }}
      >
        <div className='bg-surfaceInverse flex flex-col items-center gap-6 w-full'>
          <Image
            src='/icons/failed.svg'
            alt='failed'
            width={64}
            height={64}
            className='mt-2'
            draggable={false}
          />

          <div className='text-center space-y-2 px-2'>
            <h1 className='text-textHigh font-bold text-xl'>
              {translate('error')}
            </h1>
            <p className='text-textMid text-sm sm:text-base'>
              {translate(failReasonToKey[failReason])}
            </p>
          </div>

          <div className='w-full mt-2 px-2 pb-1'>
            <DarkButton
              fullwidth
              onClick={() => {
                setFailDialogOpen(false)
                if (failReason === 'tooManyAttempts') {
                  onBackToChoose()
                }
              }}
            >
              {translate('verify.kyc.faceRecognition.failed.button')}
            </DarkButton>
          </div>
        </div>
      </Dialog>

      <div className='flex flex-col items-center justify-between px-4 py-8 bg-surfaceLow'>
        <div className='flex flex-col items-center gap-8'>
          <div className='w-64 h-64 relative flex items-center justify-center'>
            <Image
              src='/images/faceKyc.png'
              alt='Face KYC'
              width={252}
              height={252}
            />
          </div>

          <div className='items-center text-center'>
            <p className='text-textHigh font-extrabold text-lg'>
              {translate('verify.kyc.faceRecognition.start')}
            </p>
            <p className='text-textMid text-base sm:text-lg'>
              {translate('verify.kyc.faceRecognition.start.text')}
            </p>
          </div>

          <div className='mt-4 w-full rounded-2xl bg-surfaceInverse border border-secondaryMidStroke shadow-sm p-4'>
            <p className='text-textHigh text-sm font-semibold mb-2'>
              {translate('verify.kyc.faceRecognition.guide')}
            </p>
            <div className='space-y-2 text-xs sm:text-sm text-textMid text-left px-1'>
              <div className='flex items-center gap-2'>
                <Image src='/icons/checkCircle.svg' alt='' width={16} height={16} className='shrink-0' />
                <span className='text-successMain'>
                  {translate('verify.kyc.faceRecognition.guide.text1')}
                </span>
              </div>

              <div className='flex items-start gap-2'>
                <Image src='/icons/checkCircle.svg' alt='' width={16} height={16} className='shrink-0' />
                <span className='text-successMain'>
                  {translate('verify.kyc.faceRecognition.guide.text2')}
                </span>
              </div>

              <div className='flex items-start gap-2'>
                <Image src='/icons/checkCircle.svg' alt='' width={16} height={16} className='shrink-0' />
                <span className='text-successMain'>
                  {translate('verify.kyc.faceRecognition.guide.text3')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          className='fixed md:sticky bottom-0 rounded-b-xl right-0 left-0 z-[60] md:p-6 p-4 border-t bg-surfaceLow border-secondaryMidStroke'
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
        >
          <div className='md:w-96 w-full flex'>
            <DarkButton fullwidth onClick={handleOpenCamera} disabled={loading}>
              {loading
                ? <Loader />
                : (shouldRetry ? translate('verify.kyc.faceRecognition.tryAgain') : translate('verify.kyc.faceRecognition.openCamera'))}

            </DarkButton>
          </div>
        </div>
      </div>
    </>
  )
}

export default FaceKycStep
